/// <amd-module name="CodeCoverage/Clients/Coverage/RestClient/Coverage" />
/**
 * ---------------------------------------------------------
 * Generated file, DO NOT EDIT
 * ---------------------------------------------------------
 *
 * See following wiki page for instructions on how to regenerate:
 *   https://aka.ms/azure-devops-client-generation
 *
 * Configuration file:
 *   tfs\web\extensions\testmanagement\vss-codecoverage-ci-web\clients\coverage\coverage.genclient.json
 */

import { IVssPageContext, IVssRestClient, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";
import { deserializeVssJsonObject } from "VSS/Platform/Util/Serialization";

import * as Coverage from "CodeCoverage/Clients/Coverage/Contracts/Coverage";

export interface ICoverageRestClient extends IVssRestClient {
    /**
     * \<p\>Gets the coverage information of files whose coverage changed for the specified pipeline run\</p\>
     * 
     * @param project - Project ID or project name
     * @param pipelineRunId - The ID of the pipeline run
     * @param scope - The scope for which coverage changes are required. If we do not specify it, the API returns merged coverage data for all scopes
     * @param continuationToken - continuationToken received from previous batch or null for first batch.
     */
    getChanges(project: string, pipelineRunId: number, scope?: string, continuationToken?: string): Promise<Coverage.CoverageChangeSummaryResponse>;
    /**
     * \<p\>Gets the coverage summary for the specified path and its children for the specified pipeline run\</p\>
     * 
     * @param directoryCoverageSummaryRequest - Provides properties that describe inputs for the directory coverage summary
     * @param project - Project ID or project name
     * @param pipelineRunId - The ID of the pipeline run
     */
    getDirectoryCoverageSummary(directoryCoverageSummaryRequest: Coverage.DirectoryCoverageSummaryRequest, project: string, pipelineRunId: number): Promise<Coverage.DirectoryCoverageSummary>;
    /**
     * \<p\>Gets the file coverage details for the specified file\</p\>
     * 
     * @param fileCoverageDetailsRequest - Properties that describe input for the file coverage details
     * @param project - Project ID or project name
     * @param pipelineRunId - The ID of the pipeline run
     */
    getFileCoverageDetails(fileCoverageDetailsRequest: Coverage.FileCoverageDetailsRequest, project: string, pipelineRunId: number): Promise<Coverage.FileCoverageDetails>;
    /**
     * \<p\>Gets the list of coverage scopes for the specified pipeline run\</p\>
     * 
     * @param project - Project ID or project name
     * @param pipelineRunId - The ID of the pipeline run
     */
    getScopes(project: string, pipelineRunId: number): Promise<string[]>;
    /**
     * \<p\>Gets the overall coverage summary for the specified pipeline run and coverage percentage changed from the previous successful pipeline run\</p\>
     * 
     * @param project - Project ID or project name
     * @param pipelineRunId - The ID of the pipeline run
     * @param scope - The scope for which coverage changes are required. If we do not specify it, the API returns merged coverage data for all scopes
     */
    getPipelineCoverageSummary(project: string, pipelineRunId: number, scope?: string): Promise<Coverage.PipelineCoverageSummary>;
}

class CoverageRestClient extends RestClientBase implements ICoverageRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * \<p\>Gets the coverage information of files whose coverage changed for the specified pipeline run\</p\>
     * 
     * @param project - Project ID or project name
     * @param pipelineRunId - The ID of the pipeline run
     * @param scope - The scope for which coverage changes are required. If we do not specify it, the API returns merged coverage data for all scopes
     * @param continuationToken - continuationToken received from previous batch or null for first batch.
     */
    public async getChanges(
        project: string,
        pipelineRunId: number,
        scope?: string,
        continuationToken?: string
        ): Promise<Coverage.CoverageChangeSummaryResponse> {

        const queryValues: any = {
            pipelineRunId: pipelineRunId,
            scope: scope,
            continuationToken: continuationToken
        };

        return this.beginRequest<Response>({
            apiVersion: "6.0-preview.1",
            routeTemplate: "{project}/_apis/coverage/changes",
            routeValues: {
                project: project
            },
            queryParams: queryValues,
            returnRawResponse: true
        }).then(async response => {
            const body = await response.text().then(deserializeVssJsonObject);
            const result: Coverage.CoverageChangeSummaryResponse = {
                coverageChangeSummary: <Coverage.CoverageChangeSummary>body,
                continuationToken: response.headers.get("x-ms-continuationtoken")
            };
            return result;
        });
    }

    /**
     * \<p\>Gets the coverage summary for the specified path and its children for the specified pipeline run\</p\>
     * 
     * @param directoryCoverageSummaryRequest - Provides properties that describe inputs for the directory coverage summary
     * @param project - Project ID or project name
     * @param pipelineRunId - The ID of the pipeline run
     */
    public async getDirectoryCoverageSummary(
        directoryCoverageSummaryRequest: Coverage.DirectoryCoverageSummaryRequest,
        project: string,
        pipelineRunId: number
        ): Promise<Coverage.DirectoryCoverageSummary> {

        const queryValues: any = {
            pipelineRunId: pipelineRunId
        };

        return this.beginRequest<Coverage.DirectoryCoverageSummary>({
            apiVersion: "6.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/coverage/directorysummary",
            routeValues: {
                project: project
            },
            queryParams: queryValues,
            body: directoryCoverageSummaryRequest
        });
    }

    /**
     * \<p\>Gets the file coverage details for the specified file\</p\>
     * 
     * @param fileCoverageDetailsRequest - Properties that describe input for the file coverage details
     * @param project - Project ID or project name
     * @param pipelineRunId - The ID of the pipeline run
     */
    public async getFileCoverageDetails(
        fileCoverageDetailsRequest: Coverage.FileCoverageDetailsRequest,
        project: string,
        pipelineRunId: number
        ): Promise<Coverage.FileCoverageDetails> {

        const queryValues: any = {
            pipelineRunId: pipelineRunId
        };

        return this.beginRequest<Coverage.FileCoverageDetails>({
            apiVersion: "6.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/coverage/filecoveragedetails",
            routeValues: {
                project: project
            },
            queryParams: queryValues,
            body: fileCoverageDetailsRequest
        });
    }

    /**
     * \<p\>Gets the list of coverage scopes for the specified pipeline run\</p\>
     * 
     * @param project - Project ID or project name
     * @param pipelineRunId - The ID of the pipeline run
     */
    public async getScopes(
        project: string,
        pipelineRunId: number
        ): Promise<string[]> {

        const queryValues: any = {
            pipelineRunId: pipelineRunId
        };

        return this.beginRequest<string[]>({
            apiVersion: "6.0-preview.1",
            routeTemplate: "{project}/_apis/coverage/scopes",
            routeValues: {
                project: project
            },
            queryParams: queryValues
        });
    }

    /**
     * \<p\>Gets the overall coverage summary for the specified pipeline run and coverage percentage changed from the previous successful pipeline run\</p\>
     * 
     * @param project - Project ID or project name
     * @param pipelineRunId - The ID of the pipeline run
     * @param scope - The scope for which coverage changes are required. If we do not specify it, the API returns merged coverage data for all scopes
     */
    public async getPipelineCoverageSummary(
        project: string,
        pipelineRunId: number,
        scope?: string
        ): Promise<Coverage.PipelineCoverageSummary> {

        const queryValues: any = {
            pipelineRunId: pipelineRunId,
            scope: scope
        };

        return this.beginRequest<Coverage.PipelineCoverageSummary>({
            apiVersion: "6.0-preview.1",
            routeTemplate: "{project}/_apis/coverage/summary",
            routeValues: {
                project: project
            },
            queryParams: queryValues
        });
    }

}

export const CoverageClientName = "Coverage_Coverage-Ci-WebPlatform.ICoverageRestClient";

export function getCoverageClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): ICoverageRestClient {
    return pageContext.getRestClient<ICoverageRestClient>(CoverageClientName, options);
}

RestClients.add(CoverageClientName, { 
    factory: CoverageRestClient,
    options: {
        resourceAreaId: "4e080c62-fa21-4fbc-8fef-2a10a2b38049",
        serviceInstanceType: "00000054-0000-8888-8000-000000000000"
    }
});
